package it.unibo.tuprolog.parser.exceptions

import it.unibo.tuprolog.parser.operators.OperatorDefinition
import it.unibo.tuprolog.parser.sources.Source
import it.unibo.tuprolog.parser.sources.SourceSpan
import it.unibo.tuprolog.parser.tokens.Token

/** Base class for failures encountered while constructing Prolog syntax from valid tokens. */
sealed class PrologParsingException(
    code: SyntaxErrorCode,
    source: Source,
    span: SourceSpan,
    offendingText: String?,
    expected: Set<SyntaxExpectation>,
    rulePath: List<String>,
    message: String,
) : PrologSyntaxException(
        code,
        source.id,
        span,
        offendingText,
        expected,
        rulePath,
        message,
    )

/** Reports a non-EOF token that is not accepted by the active grammar rule. */
class UnexpectedTokenException(
    source: Source,
    token: Token,
    offendingText: String,
    expected: Set<SyntaxExpectation>,
    rulePath: List<String>,
) : PrologParsingException(
        SyntaxErrorCode.UNEXPECTED_TOKEN,
        source,
        token.span,
        offendingText,
        expected,
        rulePath,
        "Unexpected token '$offendingText' at ${token.span.start.line + 1}:${token.span.start.column + 1}; " +
            "expected ${expected.joinToString { it.description }}",
    )

/** Reports EOF where the active grammar rule still requires another construct. */
class UnexpectedEndOfInputException(
    source: Source,
    token: Token,
    expected: Set<SyntaxExpectation>,
    rulePath: List<String>,
) : PrologParsingException(
        SyntaxErrorCode.UNEXPECTED_END_OF_INPUT,
        source,
        token.span,
        null,
        expected,
        rulePath,
        "Unexpected end of input at ${token.span.start.line + 1}:${token.span.start.column + 1}; " +
            "expected ${expected.joinToString { it.description }}",
    )

/**
 * Reports a prefix, infix, or postfix operator occurrence without a required operand.
 *
 * @property definition resolved operator definition
 * @property side missing operand side, either `left` or `right`
 */
class MissingOperatorOperandException(
    source: Source,
    operatorToken: Token,
    operatorText: String,
    val definition: OperatorDefinition,
    val side: String,
    rulePath: List<String>,
) : PrologParsingException(
        SyntaxErrorCode.MISSING_OPERATOR_OPERAND,
        source,
        operatorToken.span,
        operatorText,
        setOf(SyntaxExpectation("$side operand")),
        rulePath,
        "Operator '$operatorText' (${definition.specifier.name.lowercase()}, priority ${definition.priority}) " +
            "is missing its $side operand",
    )

/**
 * Reports an operand whose priority violates its operator's `x` or `y` constraint.
 *
 * @property definition resolved operator definition
 * @property operandPriority root priority of the rejected operand
 * @property side rejected operand side
 */
class OperatorPriorityException(
    source: Source,
    operatorToken: Token,
    operatorText: String,
    val definition: OperatorDefinition,
    val operandPriority: Int,
    val side: String,
    rulePath: List<String>,
) : PrologParsingException(
        SyntaxErrorCode.OPERATOR_PRIORITY_VIOLATION,
        source,
        operatorToken.span,
        operatorText,
        emptySet(),
        rulePath,
        "Operator '$operatorText' (${definition.specifier.name.lowercase()}, priority ${definition.priority}) " +
            "cannot accept a $side operand with priority $operandPriority",
    )

/**
 * Reports an occurrence for which several operator definitions remain applicable.
 *
 * @property candidates definitions that could parse the occurrence
 */
class AmbiguousOperatorUseException(
    source: Source,
    operatorToken: Token,
    operatorText: String,
    val candidates: List<OperatorDefinition>,
    rulePath: List<String>,
) : PrologParsingException(
        SyntaxErrorCode.AMBIGUOUS_OPERATOR_USE,
        source,
        operatorToken.span,
        operatorText,
        candidates.mapTo(linkedSetOf()) {
            SyntaxExpectation("${it.specifier.name.lowercase()} at priority ${it.priority}")
        },
        rulePath,
        "Ambiguous operator '$operatorText': " +
            candidates.joinToString { "${it.specifier.name.lowercase()}/${it.priority}" },
    )

/** Reports a clause that is not followed by a clause-terminating full stop. */
class MissingClauseTerminatorException(
    source: Source,
    token: Token,
    offendingText: String?,
    rulePath: List<String>,
) : PrologParsingException(
        SyntaxErrorCode.MISSING_CLAUSE_TERMINATOR,
        source,
        token.span,
        offendingText,
        setOf(SyntaxExpectation("clause-terminating full stop")),
        rulePath,
        "Expected a clause-terminating full stop at ${token.span.start.line + 1}:${token.span.start.column + 1}",
    )

/**
 * Reports input nesting deeper than [it.unibo.tuprolog.parser.ParserOptions.maximumNestingDepth].
 *
 * @property maximumDepth configured maximum depth
 */
class NestingLimitExceededException(
    source: Source,
    token: Token,
    val maximumDepth: Int,
    rulePath: List<String>,
) : PrologParsingException(
        SyntaxErrorCode.NESTING_LIMIT_EXCEEDED,
        source,
        token.span,
        null,
        emptySet(),
        rulePath,
        "Maximum parser nesting depth $maximumDepth exceeded at " +
            "${token.span.start.line + 1}:${token.span.start.column + 1}",
    )
